import { Directive, ElementRef, HostListener, inject } from '@angular/core';
import { NgControl } from '@angular/forms';
import { soloDigitos } from '../utils/documento.util';

/**
 * Directiva `soloNumeros`: fuerza que un input solo conserve dígitos.
 *
 * Limpia el valor en cada `input` (cubre tecleo, pegado y arrastrar-soltar, porque
 * todos disparan ese evento después de modificar el campo) y otra vez en `blur`.
 * Si el input está atado a un `FormControl`, escribe el valor limpio de vuelta al
 * control para que el modelo nunca vea la basura.
 *
 * Pensada para cédulas / números de documento, donde un espacio o un salto de línea
 * pegado desde Excel termina almacenado en base de datos.
 *
 * ### Uso:
 * ```html
 * <input pInputText soloNumeros formControlName="codEmp" />
 * ```
 *
 * @selector input[soloNumeros]
 */
@Directive({
    selector: 'input[soloNumeros]'
})
export class SoloNumerosDirective {
    private el = inject<ElementRef<HTMLInputElement>>(ElementRef);
    private ngControl = inject(NgControl, { optional: true, self: true });

    @HostListener('input')
    onInput(): void {
        this.limpiar();
    }

    @HostListener('blur')
    onBlur(): void {
        this.limpiar();
    }

    private limpiar(): void {
        const input = this.el.nativeElement;
        const crudo = input.value ?? '';
        // Acá sí se descartan los no-dígitos: es lo que el usuario está tecleando y lo ve pasar.
        const limpio = soloDigitos(crudo);

        if (limpio === crudo) {
            return;
        }

        input.value = limpio;
        // emitEvent: false evita re-disparar valueChanges y provocar un ciclo con este mismo listener.
        this.ngControl?.control?.setValue(limpio, { emitEvent: false });
    }
}
